import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Budget, Transaction } from '../types';

interface BudgetContextType {
  currentBudget: Budget | null;
  setCurrentBudget: React.Dispatch<React.SetStateAction<Budget | null>>;
  canNavigateNext: boolean;
  canNavigatePrevious: boolean;
  handlePreviousMonth: () => void;
  handleNextMonth: () => void;
  handleUpdateTransactionCategory: (transaction: Transaction, newCategory: string) => Promise<void>;
  handleMoveBudgetCategory: (dragIndex: number, hoverIndex: number, type: 'income' | 'expense' | 'savings') => Promise<void>;
  formatCurrency: (amount: number) => string;
  refreshBudgets: () => void;
  availableMonths: Date[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

// Service injection for testability
interface BudgetService {
  getByMonth: (month: string | Date) => Promise<Budget | null>;
  getAvailableMonths: () => Promise<Date[]>;
  update: (id: string, data: any) => Promise<Budget>;
}

// Default service implementation
let budgetService: BudgetService | null = null;

// Function to set the service (for testing)
export const setBudgetService = (service: BudgetService) => {
  budgetService = service;
};

// Lazy load the service to avoid import issues in tests
const getBudgetService = async (): Promise<BudgetService> => {
  if (budgetService) return budgetService;
  
  // Only import in non-test environment
  if (process.env.NODE_ENV !== 'test') {
    try {
      const { budgets } = await import('../services/api');
      return {
        getByMonth: async (month: string | Date) => {
          try {
            return await budgets.getByMonth(month);
          } catch (error) {
            console.error('Error fetching budget data:', error);
            return null;
          }
        },
        getAvailableMonths: async () => {
          try {
            return await budgets.getAvailableMonths();
          } catch (error) {
            console.error('Error fetching available months:', error);
            return [new Date()];
          }
        },
        update: async (id: string, data: any) => {
          return await budgets.update(id, data);
        }
      };
    } catch (error) {
      console.error('Error loading budget service:', error);
    }
  }
  
  // Fallback mock service for tests
  return {
    getByMonth: async (month: string | Date) => null,
    getAvailableMonths: async () => [new Date()],
    update: async (id: string, data: any) => data as Budget
  };
};

const BudgetContext = createContext<BudgetContextType | null>(null);

export const useBudget = () => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
};

export const BudgetProvider: React.FC<{ children: React.ReactNode; disableAutoFetch?: boolean }> = ({ 
  children, 
  disableAutoFetch = false 
}) => {
  const [currentBudget, setCurrentBudget] = useState<Budget | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableMonths, setAvailableMonths] = useState<Date[]>([]);
  const [isCreatingBudget, setIsCreatingBudget] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Add refs to prevent duplicate API calls
  const fetchInProgress = useRef(false);
  const lastFetchParams = useRef<string>('');
  const lastFetchTime = useRef<number>(0);
  const mounted = useRef(true);
  
  // Detect if we're in React StrictMode (development)
  const isStrictMode = useRef(false);
  useEffect(() => {
    isStrictMode.current = process.env.NODE_ENV === 'development';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }, []);

  const handlePreviousMonth = useCallback(() => {
    setSelectedDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setSelectedDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }, []);

  const handleSetSelectedDate = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const canNavigateNext = React.useMemo(() => {
    const currentDate = new Date();
    const selectedMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth());
    const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth());
    return selectedMonth.getTime() < currentMonth.getTime();
  }, [selectedDate]);

  const canNavigatePrevious = React.useMemo(() => {
    if (availableMonths.length === 0) return false;
    const earliestMonth = availableMonths[0];
    const selectedMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth());
    return selectedMonth.getTime() > earliestMonth.getTime();
  }, [selectedDate, availableMonths]);

  const handleUpdateTransactionCategory = useCallback(async (transaction: Transaction, newCategory: string) => {
    if (!currentBudget) return;

    // Update budget categories
    setCurrentBudget(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        categories: prev.categories.map(category => {
          if (category.name === transaction.category && category.type === transaction.type) {
            return {
              ...category,
              actualAmount: category.actualAmount - transaction.amount
            };
          }
          if (category.name === newCategory && category.type === transaction.type) {
            return {
              ...category,
              actualAmount: category.actualAmount + transaction.amount
            };
          }
          return category;
        })
      };
    });
  }, [currentBudget]);

  const handleMoveBudgetCategory = useCallback(async (dragIndex: number, hoverIndex: number, type: 'income' | 'expense' | 'savings') => {
    if (!currentBudget) return;

    const newCategories = [...currentBudget.categories];
    const typeCategories = newCategories.filter(cat => cat.type === type);
    const otherCategories = newCategories.filter(cat => cat.type !== type);
    
    const [draggedCategory] = typeCategories.splice(dragIndex, 1);
    typeCategories.splice(hoverIndex, 0, draggedCategory);

    // Update sort orders
    const updatedTypeCategories = typeCategories.map((cat, index) => ({
      ...cat,
      sortOrder: index
    }));

    const updatedCategories = [...updatedTypeCategories, ...otherCategories];

    // Optimistic update
    setCurrentBudget(prev => prev ? {
      ...prev,
      categories: updatedCategories
    } : null);

    try {
      // Update in the database
      const service = await getBudgetService();
      await service.update(currentBudget._id, {
        categories: updatedCategories
      });
    } catch (err) {
      // Revert on error
      setCurrentBudget(prev => prev ? {
        ...prev,
        categories: currentBudget.categories
      } : null);
      console.error('Error updating category order:', err);
    }
  }, [currentBudget]);

  // Function to refresh budget data
  const refreshBudgets = useCallback(() => {
    setIsCreatingBudget(false); // Reset creating flag
    setRefreshTrigger(prev => prev + 1); // Trigger useEffect to refetch data
  }, []);

  // Fetch budget data when selectedDate changes
  useEffect(() => {
    if (disableAutoFetch) return;
    
    const performFetch = async () => {
      // Skip fetching if we're on the budgets page - let that page handle its own data
      if (typeof window !== 'undefined' && window.location.pathname === '/budgets') {
        console.log('Skipping BudgetContext fetch on /budgets page - handled by BudgetList component');
        return;
      }

      // Skip if component is unmounted
      if (!mounted.current) return;

      // Create a unique key for this fetch request
      const fetchKey = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${isCreatingBudget}-${refreshTrigger}`;
      
      // Enhanced duplicate prevention for React StrictMode
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTime.current;
      
      // Prevent duplicate calls within 100ms (React StrictMode protection)
      if (fetchInProgress.current || 
          (lastFetchParams.current === fetchKey && timeSinceLastFetch < 100)) {
        console.log('Preventing duplicate budget fetch:', fetchKey, 'timeSince:', timeSinceLastFetch + 'ms');
        return;
      }

      try {
        fetchInProgress.current = true;
        lastFetchParams.current = fetchKey;
        lastFetchTime.current = now;
        
        if (process.env.NODE_ENV !== 'test') {
          console.log('Fetching budget data:', fetchKey);
        }
        
        // Check if we're on the dashboard - if so, only fetch current month's budget
        const isDashboard = typeof window !== 'undefined' && window.location.pathname === '/';
        if (process.env.NODE_ENV !== 'test') {
          console.log('Dashboard check - pathname:', window?.location?.pathname, 'isDashboard:', isDashboard);
        }
        
                 const service = await getBudgetService();
         
         // Fetch budget data for the selected month
         const monthDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
         
         const [budget, months] = await Promise.all([
           service.getByMonth(monthDate),
           service.getAvailableMonths()
         ]);

        // Only update state if component is still mounted
        if (mounted.current) {
          console.log('BudgetContext: Setting budget data:', budget ? 'found budget' : 'no budget found');
          console.log('BudgetContext: Budget data:', budget);
          console.log('BudgetContext: Available months:', months);
          setCurrentBudget(budget);
          setAvailableMonths(months);
        }
        
      } catch (err) {
        if (mounted.current) {
          console.error('Error fetching budget data:', err);
          // Don't clear current budget on error, just log it
        }
      } finally {
        fetchInProgress.current = false;
      }
    };

    performFetch();
  }, [selectedDate, refreshTrigger, disableAutoFetch]);

  const value = {
    currentBudget,
    setCurrentBudget,
    canNavigateNext,
    canNavigatePrevious,
    handlePreviousMonth,
    handleNextMonth,
    handleUpdateTransactionCategory,
    handleMoveBudgetCategory,
    formatCurrency,
    refreshBudgets,
    availableMonths,
    selectedDate,
    setSelectedDate: handleSetSelectedDate
  };

  return (
    <BudgetContext.Provider value={value}>
      {children}
    </BudgetContext.Provider>
  );
}; 